package org.wordsapp

import androidx.compose.foundation.layout.*
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.List
import androidx.compose.material.icons.filled.Create
import androidx.compose.material.icons.filled.Refresh
import androidx.compose.material.icons.filled.Settings
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import org.wordsapp.data.ApiClient
import org.wordsapp.data.WordRepository
import org.wordsapp.model.AuthState
import org.wordsapp.model.Screen
import org.wordsapp.theme.WordsAppTheme
import org.wordsapp.ui.screen.*
import org.wordsapp.viewmodel.WordViewModel

@Composable
fun App(
    onNavigateToLogin: (() -> Unit)? = null,
    appAuthToken: String? = null,
) {
    val api = remember { ApiClient() }
    val repository = remember { WordRepository(api) }
    val viewModel = remember { WordViewModel(repository) }

    val authState by viewModel.authState.collectAsState()
    val hasSpreadsheet by viewModel.hasSpreadsheet.collectAsState()
    val completedRounds by viewModel.completedRounds.collectAsState()
    var currentScreen by remember { mutableStateOf(Screen.Review) }

    LaunchedEffect(Unit) {
        viewModel.checkAuth()
    }

    LaunchedEffect(appAuthToken) {
        appAuthToken?.let { viewModel.completeAppLogin(it) }
    }

    WordsAppTheme {
        when (authState) {
            is AuthState.Loading -> {
                Box(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.Center,
                ) {
                    CircularProgressIndicator()
                }
            }
            is AuthState.NotAuthenticated -> {
                LoginScreen(
                    onGoogleLogin = {
                        onNavigateToLogin?.invoke()
                    },
                    onDemoMode = {
                        viewModel.enterDemoMode()
                    },
                )
            }
            is AuthState.Authenticated, is AuthState.Demo -> {
                LaunchedEffect(authState) {
                    if (authState is AuthState.Authenticated) {
                        viewModel.loadWords()
                    }
                }

                val spreadsheetReady = hasSpreadsheet == true

                LaunchedEffect(hasSpreadsheet) {
                    if (hasSpreadsheet == false) {
                        currentScreen = Screen.Settings
                    }
                }

                Scaffold(
                    bottomBar = {
                        Column {
                            Surface(
                                color = MaterialTheme.colorScheme.surfaceVariant,
                                modifier = Modifier.fillMaxWidth(),
                            ) {
                                Text(
                                    text = "Rounds completed: $completedRounds",
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(horizontal = 16.dp, vertical = 4.dp),
                                    style = MaterialTheme.typography.bodySmall,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                                    textAlign = TextAlign.Center,
                                )
                            }
                            NavigationBar {
                                NavigationBarItem(
                                    selected = currentScreen == Screen.Review,
                                    onClick = { if (spreadsheetReady) currentScreen = Screen.Review },
                                    icon = { Icon(Icons.Default.Refresh, contentDescription = "Review") },
                                    label = { Text("Review") },
                                    enabled = spreadsheetReady,
                                )
                                NavigationBarItem(
                                    selected = currentScreen == Screen.Practice,
                                    onClick = { if (spreadsheetReady) currentScreen = Screen.Practice },
                                    icon = { Icon(Icons.Default.Create, contentDescription = "Practice") },
                                    label = { Text("Practice") },
                                    enabled = spreadsheetReady,
                                )
                                NavigationBarItem(
                                    selected = currentScreen == Screen.Statistics || currentScreen == Screen.WeightDistribution,
                                    onClick = { if (spreadsheetReady) currentScreen = Screen.Statistics },
                                    icon = { Icon(Icons.AutoMirrored.Filled.List, contentDescription = "Statistics") },
                                    label = { Text("Statistics") },
                                    enabled = spreadsheetReady,
                                )
                                NavigationBarItem(
                                    selected = currentScreen == Screen.Settings,
                                    onClick = { currentScreen = Screen.Settings },
                                    icon = { Icon(Icons.Default.Settings, contentDescription = "Settings") },
                                    label = { Text("Settings") },
                                )
                            }
                        }
                    },
                ) { padding ->
                    Box(modifier = Modifier.padding(padding)) {
                        when (currentScreen) {
                            Screen.Practice -> PracticeScreen(
                                viewModel = viewModel,
                                onNavigateToStatistics = { currentScreen = Screen.Statistics },
                            )
                            Screen.Review -> ReviewScreen(viewModel)
                            Screen.Statistics -> StatisticsScreen(
                                viewModel = viewModel,
                                onShowWeightDistribution = { currentScreen = Screen.WeightDistribution },
                            )
                            Screen.WeightDistribution -> WeightDistributionScreen(
                                viewModel = viewModel,
                                onBack = { currentScreen = Screen.Statistics },
                            )
                            Screen.Settings -> SettingsScreen(viewModel)
                        }
                    }
                }
            }
        }
    }
}
